import { isAxiosError } from 'axios'
import client from './client'
import type {
  PayoutAccount,
  PayoutHostedLink,
  PayoutLinkPurpose,
  PayoutNotReadyError,
  PayoutSummary,
} from '@/types/payouts'

function accountBase(organisationId: number): string {
  return `/organizer/organisations/${organisationId}/payout-account`
}

export async function fetchPayoutAccount(organisationId: number): Promise<PayoutAccount> {
  const { data } = await client.get<{ data: PayoutAccount }>(accountBase(organisationId))
  return data.data
}

export async function refreshPayoutAccount(organisationId: number): Promise<PayoutAccount> {
  const { data } = await client.post<{ data: PayoutAccount }>(`${accountBase(organisationId)}/refresh`)
  return data.data
}

export async function createPayoutHostedLink(
  organisationId: number,
  purpose: PayoutLinkPurpose,
): Promise<PayoutHostedLink> {
  const { data } = await client.post<{ data: PayoutHostedLink }>(`${accountBase(organisationId)}/hosted-links`, {
    purpose,
  })
  return data.data
}

export async function fetchPayoutSummary(organisationId: number): Promise<PayoutSummary> {
  const { data } = await client.get<{ data: PayoutSummary }>(
    `/organizer/organisations/${organisationId}/finance/payouts`,
  )
  return data.data
}

/**
 * The publish gate answers 422 with { error: 'payout_account_not_ready', message }
 * instead of the validation envelope - there is no `errors` object to map.
 */
export function isPayoutNotReadyError(e: unknown): boolean {
  return (
    isAxiosError<Partial<PayoutNotReadyError>>(e) &&
    e.response?.status === 422 &&
    e.response.data?.error === 'payout_account_not_ready'
  )
}

/** Server message of a payout_account_not_ready error, or null for any other error. */
export function payoutNotReadyMessage(e: unknown): string | null {
  if (!isPayoutNotReadyError(e) || !isAxiosError<Partial<PayoutNotReadyError>>(e)) return null
  return e.response?.data?.message ?? null
}
